import { IVector2 } from '../types';

/**
 * Асинхронный sleep
 *
 * @param sleepTime задержка в миллисекундах
 */
export function sleep(sleepTime: number) {
	return new Promise<void>((resolve) => {
		setTimeout(resolve, sleepTime);
	});
}

/**
 * Разбиение числа на разряды
 *
 * @param value число
 */
export function explodeNumber(value: number, separator = ' ') {
	return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

/**
 * Возвращает случайное число в интервале [minValue, maxValue]
 *
 * @param minValue Минимальное значение
 * @param maxValue Максимальное значение
 */
export function getRandomInt(minValue: number, maxValue: number) {
	const min = Math.ceil(minValue);
	const max = Math.floor(maxValue);

	return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Выполняет линейную интерполяцию значения между двумя диапазонами
 *
 * @param value Входное значение в диапазоне [x1, x2]
 *
 * @param x1 Нижняя граница входного диапазона
 * @param x2 Верхняя граница входного диапазона
 * @param y1 Нижняя граница выходного диапазона
 * @param y2 Верхняя граница выходного диапазона
 *
 * @returns Интерполированное значение в диапазоне [y1, y2]
 */
export function linearInterpolation(value: number, x1: number, x2: number, y1: number, y2: number): number {
	return y1 + ((value - x1) * (y2 - y1)) / (x2 - x1);
}

/**
 * Возвращает случайный элемент массива
 *
 * @param array массив
 */
export function getRandomArrayElement<T>(array: Array<T>): T {
	return array[getRandomInt(0, array.length - 1)];
}

/**
 * Перемешивает элементы массива
 *
 * @param array массив
 */
export function shuffleArray<T>(array: T[]) {
	let currentIndex = array.length;
	let temporaryValue: T;
	let randomIndex: number;

	while (0 !== currentIndex) {
		randomIndex = Math.floor(Math.random() * currentIndex);
		currentIndex -= 1;

		temporaryValue = array[currentIndex];
		array[currentIndex] = array[randomIndex];
		array[randomIndex] = temporaryValue;
	}

	return array;
}

/**
 * Выдаёт элементы в случайном порядке без повторов, пока не будут выданы все, затем перемешивает заново
 */
export class ShuffledDeck<T> {
	private items: readonly T[] = [];
	private remaining: T[] = [];

	public setItems(items: readonly T[]): void {
		this.items = items;
		this.remaining = [];
	}

	public next(): T {
		if (this.remaining.length === 0) {
			this.remaining = shuffleArray([...this.items]);
		}

		return this.remaining.pop()!;
	}
}

/**
 * Вызывает асинхронную функцию с ограничением по времени выполнения.
 * Если время истекает до завершения промиса, возвращает undefined.
 *
 * @param taskPromise асинхронная функция для выполнения
 * @param timeLimit максимальное время выполнения в мс
 * @returns Результат промиса или undefined при таймауте
 */
export async function callTimedAsync<T>(taskPromise: () => Promise<T>, timeLimit: number): Promise<T | undefined> {
	let timeoutId!: ReturnType<typeof setTimeout>;

	const timeout = new Promise<undefined>((resolve) => {
		timeoutId = setTimeout(() => resolve(undefined), timeLimit);
	});

	const response = await Promise.race([taskPromise(), timeout]);

	clearTimeout(timeoutId);

	return response;
}

export function debounce<R, A extends any[]>(fn: (...args: A) => R, delay: number, immediate: boolean) {
	let timeout: ReturnType<typeof setTimeout> | undefined;

	return (...args: A) => {
		const later = () => {
			timeout = undefined;
			if (!immediate) {
				fn(...args);
			}
		};

		const callNow: boolean = immediate && !timeout;

		clearTimeout(timeout);
		timeout = setTimeout(later, delay);

		if (callNow) {
			fn(...args);
		}
	};
}

export function throttle<R, A extends any[]>(fn: (...args: A) => R, delay: number) {
	let shouldWait = false;

	return (...args: A) => {
		if (!shouldWait) {
			fn(...args);
			shouldWait = true;
			setTimeout(() => (shouldWait = false), delay);
		}
	};
}

export function numWord(value: number, words: string[]) {
	value = Math.abs(value) % 100;
	const num = value % 10;
	if (value > 10 && value < 20) return words[2];
	if (num > 1 && num < 5) return words[1];
	if (num == 1) return words[0];
	return words[2];
}

export function findRotation(position1: IVector2, position2: IVector2) {
	const deltaX = position2.x - position1.x;
	const deltaY = position2.y - position1.y;
	const radians = -Math.atan2(deltaX, deltaY);
	const degrees = (radians * 180) / Math.PI;
	return degrees < 0 ? degrees + 360 : degrees;
}

/** Получить массив всех значений **числового** енама */
export function numericEnumValues<T extends object>(enumName: T): T[keyof T][] {
	const enumEntries = Object.entries(enumName);
	return enumEntries.slice(enumEntries.length / 2).map((entry) => entry[1]);
}

/** Получить массив всех ключей **числового** енама */
export function numericEnumKeys<T extends object>(enumName: T): string[] {
	const enumEntries = Object.entries(enumName);
	return enumEntries.slice(enumEntries.length / 2).map((entry) => entry[0]);
}

/** Получить остаток от деления числа **m** на **n** (всегда неотрицательный) */
export function mod(n: number, m: number): number {
	return ((n % m) + m) % m;
}
